// src/components/practices/MagicalToDoList.js
// Magical To-Do List — write down what needs doing, then move through each item imagining it's
// already taken care of. Three phases: list (build the items) -> steps (one item at a time, same
// ring-counter + thank-you-tap interaction as the daily Gratitudes) -> complete. There was
// previously a fourth, leading 'intro' phase repeating the detail screen's (AugustPracticeDetail)
// tagline/description under a redundant "Start Your List" CTA — removed; it wasn't a real step
// (never counted toward the step total), just an extra tap with no new information.
// Distinct from Journey day 18 ("The Magical To-Do List"), which is a plain 10-gratitudes entry
// on that theme, not this interactive flow — see FLOW_TYPES in content.js (flowType is unused
// today, so there's no actual UI collision, just a naming echo worth a comment).
import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../../supabaseClient'
import PageHeader from '../ui/PageHeader'
import PrimaryButton from '../ui/PrimaryButton'
import PracticeStepRing from './shared/PracticeStepRing'
import PracticeThankYouTap from './shared/PracticeThankYouTap'
import PracticeComplete from './shared/PracticeComplete'
import './shared/PracticeHeading.css'
import './MagicalToDoList.css'

const REREAD_MS = 2000

export default function MagicalToDoList({ practice, session, city, onComplete, onBack, augustTheme, onAugustThemeChange }) {
  const [phase, setPhase] = useState('list') // 'list' | 'steps' | 'complete'
  const [items, setItems] = useState(['', '', '', ''])
  const [steps, setSteps] = useState([]) // built from non-empty items once "Turn Into Gratitude" is tapped
  const [itemIndex, setItemIndex] = useState(0)
  const [gratitudes, setGratitudes] = useState([])
  const [thanksCounts, setThanksCounts] = useState([])
  const [reread, setReread] = useState(false)
  const [saving, setSaving] = useState(false)
  const rereadTimer = useRef(null)

  useEffect(() => () => clearTimeout(rereadTimer.current), [])

  const updateItem = (i, value) => {
    const next = [...items]; next[i] = value; setItems(next)
  }
  const addItem = () => setItems([...items, ''])
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i))

  const handleTurnIntoGratitude = () => {
    const nonEmpty = items.map(s => s.trim()).filter(Boolean)
    if (nonEmpty.length === 0) return
    setSteps(nonEmpty)
    setGratitudes(nonEmpty.map(() => ''))
    setThanksCounts(nonEmpty.map(() => 0))
    setItemIndex(0)
    setPhase('steps')
  }

  const currentGratitude = gratitudes[itemIndex] || ''
  const currentThanks = thanksCounts[itemIndex] || 0
  const typed = currentGratitude.trim().length > 2
  const isReady = typed && currentThanks >= 3
  const isLastStep = itemIndex === steps.length - 1
  const eyebrow = `Day ${practice.unlockDay} · ${practice.name}`

  const tapThanks = () => {
    if (currentThanks >= 3) return
    const nextCounts = [...thanksCounts]
    nextCounts[itemIndex] = currentThanks + 1
    setThanksCounts(nextCounts)
    if (nextCounts[itemIndex] < 3) {
      setReread(true)
      clearTimeout(rereadTimer.current)
      rereadTimer.current = setTimeout(() => setReread(false), REREAD_MS)
    }
  }

  const finishPractice = async (finalGratitudes) => {
    setSaving(true)
    if (session) {
      await supabase.from('practice_completions').insert({
        user_id: session.user.id,
        city: city || 'august',
        practice_id: practice.id,
        payload: { items: steps, gratitudes: finalGratitudes },
      })
    }
    setSaving(false)
    setPhase('complete')
  }

  const handleNext = () => {
    if (!isReady || saving) return
    if (isLastStep) {
      finishPractice(gratitudes)
      return
    }
    setReread(false)
    clearTimeout(rereadTimer.current)
    setItemIndex(itemIndex + 1)
  }

  if (phase === 'list') {
    const hasContent = items.some(s => s.trim())
    return (
      <div className="practice-screen" data-city="august">
        <PageHeader
          eyebrow={eyebrow} title="Your List"
          showBack onBack={onBack}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />
        <div className="practice-screen-body">
          <div className="todo-list-items">
            {items.map((item, i) => (
              <div className="todo-list-row" key={i}>
                <span className="todo-list-bullet">○</span>
                <input
                  type="text"
                  className="todo-list-input"
                  placeholder="Something you need to do…"
                  value={item}
                  onChange={e => updateItem(i, e.target.value)}
                />
                {items.length > 1 && (
                  <button type="button" className="todo-list-remove" aria-label="Remove item" onClick={() => removeItem(i)}>×</button>
                )}
              </div>
            ))}
          </div>

          <button type="button" className="todo-list-add" onClick={addItem}>+ Add another</button>

          <PrimaryButton onClick={hasContent ? handleTurnIntoGratitude : undefined} disabled={!hasContent}>
            <span>Turn Into Gratitude</span>
            <span>→</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  if (phase === 'steps') {
    return (
      <div className="practice-screen" data-city="august">
        <PageHeader
          eyebrow={eyebrow}
          title="Turn Into Gratitude"
          descriptor={`Step ${itemIndex + 1} of ${steps.length}`}
          showBack onBack={onBack}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />
        <div className="practice-screen-body">
          <PracticeStepRing current={itemIndex + 1} total={steps.length} />

          <h3 className="practice-step-label">{steps[itemIndex]}</h3>
          <p className="practice-question">Imagine it's already taken care of. What are you grateful for?</p>

          <div className="stops-input-row">
            <textarea
              className="stops-input"
              placeholder="I'm grateful for…"
              value={currentGratitude}
              onChange={e => {
                const next = [...gratitudes]; next[itemIndex] = e.target.value; setGratitudes(next)
              }}
            />
          </div>

          <PracticeThankYouTap
            count={currentThanks}
            reread={reread}
            canTap={typed}
            onTap={tapThanks}
            writtenText={currentGratitude}
          />

          <PrimaryButton onClick={isReady ? handleNext : undefined} disabled={!isReady || saving}>
            <span>{isLastStep ? 'Complete List' : 'Next'}</span>
            <span>→</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  // phase === 'complete'
  return (
    <PracticeComplete
      headline="Practice Complete"
      subline={`${steps.length} intention${steps.length === 1 ? '' : 's'} turned into gratitude.`}
      onDone={onComplete}
      showSaveToMoments
    />
  )
}
